import Icon from '@mdi/react';
import { mdiMapMarkerRadius, mdiLayersTriple, mdiAccessPointNetwork } from '@mdi/js';
import InteractivePhoneFrame from './InteractivePhoneFrame';

const pillars = [
  {
    icon: mdiMapMarkerRadius,
    heading: 'Real places, real people',
    text: 'Pinned to where it happened. See it through local eyes.',
  },
  {
    icon: mdiLayersTriple,
    heading: 'Layer communities',
    text: 'Location-based communities you actually care about.',
  },
  {
    icon: mdiAccessPointNetwork,
    heading: 'Live and in the moment',
    text: 'Livestream to the map. Catch it before it\'s over.',
  },
];

function HeroSection() {
  return (
    <section style={styles.section} data-what-is-section>
      <div style={styles.glowWarm} />
      <div style={styles.glowCool} />
      <div style={styles.glowAccent} />
      <div style={styles.container}>
        <div style={styles.content}>
          <h1 style={styles.heading}>hereabout</h1>
          <p style={styles.tagline}>
            See what's <em>actually</em> happening.
          </p>
          <p style={styles.lede}>
            A social network built on the map. Every photo, video, and livestream is tied to a real place, straight from the camera. <span style={{ fontWeight: 500 }}>No edits, no AI.</span> See the world through the eyes of people who are actually there.
          </p>
          <div style={styles.pillars}>
            {pillars.map((pillar) => (
              <div key={pillar.heading} style={styles.card}>
                <div style={styles.cardIcon}>
                  <Icon path={pillar.icon} size={1.2} color="#dc2626" />
                </div>
                <h2 style={styles.cardHeading}>{pillar.heading}</h2>
                <p style={styles.cardText}>{pillar.text}</p>
              </div>
            ))}
          </div>
        </div>
        <div style={styles.phoneWrapper}>
          <InteractivePhoneFrame isMobile={false} compact />
        </div>
      </div>
    </section>
  );
}

export default HeroSection;

const styles = {
  section: {
    padding: '72px 40px 40px 40px',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative' as const,
    overflow: 'hidden' as const,
  },

  glowWarm: {
    position: 'absolute' as const,
    top: '15%',
    left: '-5%',
    width: '50%',
    height: '70%',
    background: 'radial-gradient(ellipse at center, rgba(252, 44, 3, 0.045) 0%, rgba(252, 44, 3, 0) 70%)',
    pointerEvents: 'none' as const,
    zIndex: 0,
  },

  glowCool: {
    position: 'absolute' as const,
    bottom: '-10%',
    right: '5%',
    width: '45%',
    height: '60%',
    background: 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.03) 0%, rgba(59, 130, 246, 0) 70%)',
    pointerEvents: 'none' as const,
    zIndex: 0,
  },

  glowAccent: {
    position: 'absolute' as const,
    top: '-5%',
    right: '30%',
    width: '30%',
    height: '40%',
    background: 'radial-gradient(ellipse at center, rgba(252, 44, 3, 0.025) 0%, rgba(252, 44, 3, 0) 70%)',
    pointerEvents: 'none' as const,
    zIndex: 0,
  },

  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '200px',
    width: '100%',
    position: 'relative' as const,
    zIndex: 1,
  },

  content: {
    flex: '1 1 0%',
    minWidth: 0,
  },

  heading: {
    fontSize: '3.5rem',
    fontWeight: '400' as const,
    color: '#1e293b',
    margin: '0 0 32px 0',
    lineHeight: '1.2',
    letterSpacing: '-0.02em',
    fontFamily: '"Maven Pro", sans-serif',
    textAlign: 'left' as const,
  },

  tagline: {
    fontSize: '1.5rem',
    fontWeight: '700' as const,
    color: '#fc2c03',
    margin: '0 0 32px 0',
    letterSpacing: '-0.01em',
    textAlign: 'left' as const,
  },

  lede: {
    fontSize: '1.1rem',
    lineHeight: '1.7',
    color: '#475569',
    margin: '0 0 48px 0',
    maxWidth: '100%',
    textAlign: 'left' as const,
  },

  pillars: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '56px',
  },

  card: {
    background: 'transparent',
    padding: '0',
    textAlign: 'center' as const,
  },

  cardIcon: {
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px',
  },

  cardHeading: {
    fontSize: '0.95rem',
    fontWeight: '600' as const,
    color: '#1e293b',
    margin: '0 0 10px 0',
    letterSpacing: '-0.01em',
  },

  cardText: {
    fontSize: '0.85rem',
    lineHeight: '1.5',
    color: '#64748b',
    margin: '0',
  },

  phoneWrapper: {
    flex: '0 1 auto',
    maxWidth: '35%',
    overflow: 'visible' as const,
  },
};
